import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MenuItem } from '@/components/MenuItemCard';
import { useToast } from '@/hooks/use-toast';
import { useTenant } from './TenantContext';
import { ProductTag } from '@/pages/admin/Tags';
import { Company } from '@/data/mockCompanies';
// Utility functions to map Supabase data to interface types
const mapSupabaseMenuItemToMenuItem = (supabaseItem: any): MenuItem => ({
  id: supabaseItem.id,
  name: supabaseItem.name,
  description: supabaseItem.description || '',
  price: supabaseItem.price,
  image: supabaseItem.image_url || '',
  category: supabaseItem.category_id,
  preparationTime: supabaseItem.preparation_time || '',
  tags: supabaseItem.tags || [],
  isFeatured: supabaseItem.is_featured || false,
  order_index: supabaseItem.order_index,
  unit: supabaseItem.unit || 'unidade',
  isAvailable: supabaseItem.is_available !== undefined ? supabaseItem.is_available : true,
});

const mapMenuItemToSupabaseInsert = (item: Omit<MenuItem, 'id'>, companyId: string) => ({
  name: item.name,
  name_en: (item as any).name_en,
  name_es: (item as any).name_es,
  description: item.description,
  description_en: (item as any).description_en,
  description_es: (item as any).description_es,
  price: item.price,
  image_url: item.image,
  category_id: item.category,
  preparation_time: item.preparationTime,
  company_id: companyId,
  order_index: item.order_index || 0,
  unit: item.unit || 'unidade',
  is_available: item.isAvailable !== undefined ? item.isAvailable : true,
  is_featured: item.isFeatured || false,
});

const mapMenuItemToSupabaseUpdate = (updates: Partial<MenuItem>) => {
  const mapped: any = {};
  if (updates.name !== undefined) mapped.name = updates.name;
  if ((updates as any).name_en !== undefined) mapped.name_en = (updates as any).name_en;
  if ((updates as any).name_es !== undefined) mapped.name_es = (updates as any).name_es;
  if (updates.description !== undefined) mapped.description = updates.description;
  if ((updates as any).description_en !== undefined) mapped.description_en = (updates as any).description_en;
  if ((updates as any).description_es !== undefined) mapped.description_es = (updates as any).description_es;
  if (updates.price !== undefined) mapped.price = updates.price;
  if (updates.image !== undefined) mapped.image_url = updates.image;
  if (updates.category !== undefined) mapped.category_id = updates.category;
  if (updates.preparationTime !== undefined) mapped.preparation_time = updates.preparationTime;
  if (updates.order_index !== undefined) mapped.order_index = updates.order_index;
  if (updates.unit !== undefined) mapped.unit = updates.unit;
  if (updates.isAvailable !== undefined) mapped.is_available = updates.isAvailable;
  if (updates.isFeatured !== undefined) mapped.is_featured = updates.isFeatured;
  return mapped;
};

export interface CompanySettings {
  name: string;
  email: string;
  phone: string;
  address: string;
  workingHours: string;
  logoUrl: string;
  banner_image_url?: string;
  instagram_description?: string;
  theme_mode?: 'light' | 'dark';
  enabled_layouts?: string[];
  // Individual address fields
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  // Additional company fields  
  legal_name?: string;
  business_type?: string;
  document_type?: string;
  document_number?: string;
  // Social media and external links
  instagram_url?: string;
  ifood_url?: string;
  website_url?: string;
  whatsapp_number?: string;
  facebook_url?: string;
}

export interface ThemeSettings {
  primaryColor: string;
  accentColor: string;
}

export interface QRCodeSettings {
  url: string;
  size: number;
  includeCompanyName: boolean;
  includeFrameText: boolean;
  frameText: string;
  transparentBackground: boolean;
}

export interface LayoutSettings {
  layoutType: 'cards' | 'list' | 'story' | 'magazine' | 'book' | 'carousel' | 'premium';
}

export interface Category {
  id: string;
  name: string;
  name_en?: string;
  name_es?: string;
  description?: string;
  description_en?: string;
  description_es?: string;
  image_url?: string;
  order_index: number;
  is_active: boolean;
  company_id: string;
  created_at: string;
  updated_at: string;
}

export interface ProductComplement {
  id: string;
  company_id: string;
  name: string;
  description?: string;
  price: number;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AdminContextType {
  menuItems: MenuItem[];
  adminCategories: Category[];
  productTags: ProductTag[];
  productComplements: ProductComplement[];
  companySettings: CompanySettings;
  themeSettings: ThemeSettings;
  qrCodeSettings: QRCodeSettings;
  layoutSettings: LayoutSettings;
  addMenuItem: (item: Omit<MenuItem, 'id'>, complementIds?: string[]) => Promise<void>;
  updateMenuItem: (id: string, updates: Partial<MenuItem>, complementIds?: string[]) => Promise<void>;
  deleteMenuItem: (id: string) => Promise<void>;
  toggleMenuItemStatus: (id: string) => Promise<void>;
  addCategory: (category: Omit<Category, 'id' | 'order_index'>) => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  reorderCategories: (categories: { id: string; order_index: number }[]) => Promise<void>;
  addProductTag: (tag: Omit<ProductTag, 'id' | 'company_id' | 'order_index' | 'is_active' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProductTag: (id: string, updates: Partial<ProductTag>) => Promise<void>;
  deleteProductTag: (id: string) => Promise<void>;
  reorderProductTags: (tags: { id: string; order_index: number }[]) => Promise<void>;
  addProductComplement: (complementData: Omit<ProductComplement, 'id' | 'company_id' | 'order_index' | 'is_active' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProductComplement: (complementId: string, complementData: Partial<Omit<ProductComplement, 'id' | 'company_id' | 'created_at' | 'updated_at'>>) => Promise<void>;
  deleteProductComplement: (complementId: string) => Promise<void>;
  toggleProductComplementStatus: (complementId: string) => Promise<void>;
  updateCompanySettings: (settings: Partial<CompanySettings>) => Promise<void>;
  updateThemeSettings: (settings: Partial<ThemeSettings>) => Promise<void>;
  updateQRCodeSettings: (settings: Partial<QRCodeSettings>) => Promise<void>;
  updateLayoutSettings: (settings: Partial<LayoutSettings>) => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);


export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentCompany, updateCompanyData, refreshCompany } = useTenant();
  
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [adminCategories, setAdminCategories] = useState<Category[]>([]);
  const [productTags, setProductTags] = useState<ProductTag[]>([]);
  const [productComplements, setProductComplements] = useState<ProductComplement[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: '',
    email: '',
    phone: '',
    address: '',
    workingHours: '',
    logoUrl: '',
  });
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>({
    primaryColor: '#E53E3E',
    accentColor: '#C53030',
  });
  const [qrCodeSettings, setQrCodeSettings] = useState<QRCodeSettings>({
    url: '',
    size: 200,
    includeCompanyName: true,
    includeFrameText: true,
    frameText: 'Escaneie para ver nosso cardápio AliMenu',
    transparentBackground: false,
  });
  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>({
    layoutType: 'cards',
  });

  const { toast } = useToast();

  // Load data when company changes
  useEffect(() => {
    if (!currentCompany?.id) return;

    const loadData = async () => {
      // Load menu items with tags
      const { data: menuData, error: menuError } = await supabase
        .from('menu_items')
        .select(`
          *,
          menu_item_tags(
            product_tags(*)
          )
        `)
        .eq('company_id', currentCompany.id)
        .order('order_index', { ascending: true });

      if (menuError) {
        console.error('Error loading menu items:', menuError);
      } else {
        // Map Supabase data to MenuItem interface with tags
        const mappedMenuItems = (menuData || []).map((item: any) => ({
          ...mapSupabaseMenuItemToMenuItem(item),
          tags: item.menu_item_tags?.map((tagRelation: any) => tagRelation.product_tags) || []
        }));
        setMenuItems(mappedMenuItems);
      }

      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('order_index', { ascending: true });

      if (categoriesError) {
        console.error('Error loading categories:', categoriesError);
        toast({
          title: 'Erro',
          description: 'Erro ao carregar categorias.',
          variant: 'destructive',
        });
      } else {
        setAdminCategories(categoriesData || []);
      }

      // Load product tags
      const { data: tagsData, error: tagsError } = await supabase
        .from('product_tags')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('order_index', { ascending: true });

      if (tagsError) {
        console.error('Error loading product tags:', tagsError);
        // Don't show error toast for new companies that might not have tags yet
        // Only show error if it's not a "no rows found" error
        if (tagsError.code !== 'PGRST116') {
          toast({
            title: 'Erro',
            description: 'Erro ao carregar tags de produtos.',
            variant: 'destructive',
          });
        }
      } else {
        setProductTags(tagsData || []);
        console.log('Product tags loaded:', tagsData?.length || 0);
      }

      // Load product complements
      const { data: complementsData, error: complementsError } = await supabase
        .from('product_complements')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('order_index', { ascending: true });

      if (complementsError) {
        console.error('Error loading product complements:', complementsError);
        if (complementsError.code !== 'PGRST116') {
          toast({
            title: 'Erro',
            description: 'Erro ao carregar complementos.',
            variant: 'destructive',
          });
        }
      } else {
        setProductComplements(complementsData || []);
        console.log('Product complements loaded:', complementsData?.length || 0);
      }

      // Load company settings
      setCompanySettings({
        name: currentCompany.name || '',
        email: currentCompany.email || '',
        phone: currentCompany.phone || '',
        address: currentCompany.address || '',
        workingHours: (currentCompany as any).working_hours || (currentCompany as any).workingHours || '',
        logoUrl: (currentCompany as any).logo_url || (currentCompany as any).logoUrl || '',
        instagram_description: (currentCompany as any).instagram_description || '',
        banner_image_url: (currentCompany as any).banner_image_url || '',
        theme_mode: (currentCompany as any).theme_mode || 'dark',
        enabled_layouts: (currentCompany as any).enabled_layouts || undefined,
        // Individual address fields
        street: (currentCompany as any).street || '',
        number: (currentCompany as any).number || '',
        complement: (currentCompany as any).complement || '',
        neighborhood: (currentCompany as any).neighborhood || '',
        city: (currentCompany as any).city || '',
        state: (currentCompany as any).state || '',
        zip_code: (currentCompany as any).zip_code || '',
        // Additional company fields
        legal_name: (currentCompany as any).legal_name || '',
        business_type: (currentCompany as any).business_type || '',
        document_type: (currentCompany as any).document_type || '',
        document_number: (currentCompany as any).document_number || '',
      });

      // Load theme settings
      setThemeSettings({
        primaryColor: (currentCompany as any).theme_primary_color || (currentCompany as any).theme?.primaryColor || '#3B82F6',
        accentColor: (currentCompany as any).theme_accent_color || (currentCompany as any).theme?.accentColor || '#10B981',
      });

      // Load layout settings
      setLayoutSettings({
        layoutType: (currentCompany as any).layout_type || 'cards',
      });

      // Load QR code settings
      const { data: qrData, error: qrError } = await supabase
        .from('qr_code_settings')
        .select('*')
        .eq('company_id', currentCompany.id)
        .maybeSingle();

      if (qrError) {
        console.error('Error loading QR code settings:', qrError);
      } else if (qrData) {
        setQrCodeSettings({
          url: qrData.url || `${window.location.protocol}//${window.location.host}/${currentCompany.slug}`,
          size: qrData.size || 200,
          includeCompanyName: qrData.include_company_name ?? true,
          includeFrameText: qrData.include_frame_text ?? true,
          frameText: qrData.frame_text || 'Escaneie para ver nosso cardápio AliMenu',
          transparentBackground: qrData.transparent_background ?? false,
        });
      } else {
        // Set default values with auto-generated URL if no settings exist
        setQrCodeSettings({
          url: `${window.location.protocol}//${window.location.host}/${currentCompany.slug}`,
          size: 200,
          includeCompanyName: true,
          includeFrameText: true,
          frameText: 'Escaneie para ver nosso cardápio AliMenu',
          transparentBackground: false,
        });
      }
    };

    loadData();
  }, [currentCompany?.id, toast]);

  // Menu Items Management
  const addMenuItem = async (itemData: Omit<MenuItem, 'id'>, complementIds?: string[]) => {
    if (!currentCompany?.id) return;

    try {
      const maxOrder = Math.max(...menuItems.map(item => item.order_index || 0), -1);
      const itemDataWithOrder = { ...itemData, order_index: maxOrder + 1 };
      
      const supabaseData = mapMenuItemToSupabaseInsert(itemDataWithOrder, currentCompany.id);
      
      const { data, error } = await supabase
        .from('menu_items')
        .insert(supabaseData)
        .select()
        .single();

      if (error) throw error;

      // Save tags if provided
      if (itemData.tags && itemData.tags.length > 0) {
        const tagInserts = itemData.tags.map(tag => ({
          menu_item_id: data.id,
          product_tag_id: tag.id
        }));

        const { error: tagError } = await supabase
          .from('menu_item_tags')
          .insert(tagInserts);

        if (tagError) throw tagError;
      }

      // Save complements if provided
      if (complementIds && complementIds.length > 0) {
        const complementInserts = complementIds.map(complementId => ({
          menu_item_id: data.id,
          complement_id: complementId
        }));

        const { error: complementError } = await supabase
          .from('menu_item_complements')
          .insert(complementInserts);

        if (complementError) throw complementError;
      }

      // Map back to MenuItem and add to state
      const mappedItem = { ...mapSupabaseMenuItemToMenuItem(data), tags: itemData.tags || [] };
      setMenuItems(prev => [...prev, mappedItem]);
      
      toast({
        title: 'Sucesso',
        description: 'Produto adicionado com sucesso!',
      });
    } catch (error) {
      console.error('Error adding menu item:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao adicionar produto.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const toggleMenuItemStatus = async (id: string) => {
    try {
      const currentItem = menuItems.find(item => item.id === id);
      if (!currentItem) return;

      const newStatus = !currentItem.isAvailable;
      
      const { error } = await supabase
        .from('menu_items')
        .update({ is_available: newStatus })
        .eq('id', id);

      if (error) throw error;

      setMenuItems(prev => prev.map(item => 
        item.id === id ? { ...item, isAvailable: newStatus } : item
      ));
      
      toast({
        title: 'Sucesso',
        description: `Produto ${newStatus ? 'ativado' : 'desativado'} com sucesso!`,
      });
    } catch (error) {
      console.error('Error toggling menu item status:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao alterar status do produto.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateMenuItem = async (id: string, updates: Partial<MenuItem>, complementIds?: string[]) => {
    try {
      const supabaseUpdates = mapMenuItemToSupabaseUpdate(updates);
      
      const { data, error } = await supabase
        .from('menu_items')
        .update(supabaseUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update tags if provided
      if (updates.tags !== undefined) {
        // Remove existing tags
        await supabase
          .from('menu_item_tags')
          .delete()
          .eq('menu_item_id', id);

        // Add new tags
        if (updates.tags.length > 0) {
          const tagInserts = updates.tags.map(tag => ({
            menu_item_id: id,
            product_tag_id: tag.id
          }));

          const { error: tagError } = await supabase
            .from('menu_item_tags')
            .insert(tagInserts);

          if (tagError) throw tagError;
        }
      }

      // Update complements if provided
      if (complementIds !== undefined) {
        // Remove existing complements
        await supabase
          .from('menu_item_complements')
          .delete()
          .eq('menu_item_id', id);

        // Add new complements
        if (complementIds.length > 0) {
          const complementInserts = complementIds.map(complementId => ({
            menu_item_id: id,
            complement_id: complementId
          }));

          const { error: complementError } = await supabase
            .from('menu_item_complements')
            .insert(complementInserts);

          if (complementError) throw complementError;
        }
      }

      // Map back to MenuItem and update state
      const currentItem = menuItems.find(item => item.id === id);
      const mappedItem = { 
        ...mapSupabaseMenuItemToMenuItem(data), 
        tags: updates.tags !== undefined ? updates.tags : (currentItem?.tags || [])
      };
      setMenuItems(prev => prev.map(item => item.id === id ? mappedItem : item));
      
      toast({
        title: 'Sucesso',
        description: 'Produto atualizado com sucesso!',
      });
    } catch (error) {
      console.error('Error updating menu item:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar produto.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteMenuItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMenuItems(prev => prev.filter(item => item.id !== id));
      
      toast({
        title: 'Sucesso',
        description: 'Produto excluído com sucesso!',
      });
    } catch (error) {
      console.error('Error deleting menu item:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir produto.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Categories Management
  const addCategory = async (categoryData: Omit<Category, 'id' | 'order_index'>) => {
    if (!currentCompany?.id) return;

    try {
      const maxOrder = Math.max(...adminCategories.map(cat => cat.order_index), -1);
      
      const { data, error } = await supabase
        .from('categories')
        .insert({
          ...categoryData,
          company_id: currentCompany.id,
          order_index: maxOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;

      setAdminCategories(prev => [...prev, data]);
      
      toast({
        title: 'Sucesso',
        description: 'Categoria criada com sucesso!',
      });
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao criar categoria.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateCategory = async (id: string, updates: Partial<Category>) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setAdminCategories(prev => prev.map(cat => cat.id === id ? data : cat));
      
      toast({
        title: 'Sucesso',
        description: 'Categoria atualizada com sucesso!',
      });
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar categoria.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setAdminCategories(prev => prev.filter(cat => cat.id !== id));
      
      toast({
        title: 'Sucesso',
        description: 'Categoria excluída com sucesso!',
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir categoria.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const reorderCategories = async (categories: { id: string; order_index: number }[]) => {
    try {
      // Update order in database
      const updates = categories.map(({ id, order_index }) => 
        supabase
          .from('categories')
          .update({ order_index })
          .eq('id', id)
      );

      await Promise.all(updates);

      // Update local state
      setAdminCategories(prev => {
        const newCategories = [...prev];
        categories.forEach(({ id, order_index }) => {
          const catIndex = newCategories.findIndex(cat => cat.id === id);
          if (catIndex !== -1) {
            newCategories[catIndex] = { ...newCategories[catIndex], order_index };
          }
        });
        return newCategories.sort((a, b) => a.order_index - b.order_index);
      });
    } catch (error) {
      console.error('Error reordering categories:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao reordenar categorias.',
        variant: 'destructive',
      });
    }
  };

  // Theme Settings
  const updateThemeSettings = async (newSettings: Partial<ThemeSettings>) => {
    setThemeSettings(prev => ({ ...prev, ...newSettings }));

    // NOTE: Color application removed - only save to database
    // Colors will be applied by TenantContext when viewing public menu

    // Persist to Supabase with fallback to direct update
    if (currentCompany?.id) {
      try {
        console.log('💾 Salvando tema no Supabase:', {
          primaryColor: newSettings.primaryColor,
          accentColor: newSettings.accentColor
        });

        // Try RPC function first
        if (updateCompanyData) {
          try {
            await updateCompanyData({
              theme: {
                primaryColor: newSettings.primaryColor,
                accentColor: newSettings.accentColor,
              }
            });
            console.log('✅ Tema salvo via RPC!');
            return;
          } catch (rpcError) {
            console.warn('⚠️ RPC falhou, tentando query direta:', rpcError);
          }
        }

        // Fallback: Direct Supabase update
        const { error } = await supabase
          .from('companies')
          .update({
            theme_primary_color: newSettings.primaryColor,
            theme_accent_color: newSettings.accentColor,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentCompany.id);

        if (error) {
          throw error;
        }

        console.log('✅ Tema salvo via query direta!');
        
        // Force refresh company data
        if (refreshCompany) {
          await refreshCompany();
        }
        
      } catch (error) {
        console.error('❌ Erro ao salvar tema:', error);
        const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
        throw new Error(`Falha ao salvar tema: ${errorMsg}`);
      }
    }
  };

  // Product Tags Management
  const addProductTag = async (tagData: Omit<ProductTag, 'id' | 'company_id' | 'order_index' | 'is_active' | 'created_at' | 'updated_at'>) => {
    if (!currentCompany?.id) return;

    try {
      const maxOrder = Math.max(...productTags.map(tag => tag.order_index), -1);
      
      const { data, error } = await supabase
        .from('product_tags')
        .insert({
          ...tagData,
          company_id: currentCompany.id,
          order_index: maxOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;

      setProductTags(prev => [...prev, data]);
      
      toast({
        title: 'Sucesso',
        description: 'Tag criada com sucesso!',
      });
    } catch (error) {
      console.error('Error adding product tag:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao criar tag.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateProductTag = async (id: string, updates: Partial<ProductTag>) => {
    try {
      const { data, error } = await supabase
        .from('product_tags')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setProductTags(prev => prev.map(tag => tag.id === id ? data : tag));
      
      toast({
        title: 'Sucesso',
        description: 'Tag atualizada com sucesso!',
      });
    } catch (error) {
      console.error('Error updating product tag:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar tag.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteProductTag = async (id: string) => {
    try {
      // First, remove tag from all menu items
      const { error: removeError } = await supabase
        .from('menu_item_tags')
        .delete()
        .eq('product_tag_id', id);

      if (removeError) throw removeError;

      // Then delete the tag
      const { error } = await supabase
        .from('product_tags')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setProductTags(prev => prev.filter(tag => tag.id !== id));
      
      toast({
        title: 'Sucesso',
        description: 'Tag excluída com sucesso!',
      });
    } catch (error) {
      console.error('Error deleting product tag:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir tag.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const reorderProductTags = async (tags: { id: string; order_index: number }[]) => {
    try {
      // Update order in database
      const updates = tags.map(({ id, order_index }) => 
        supabase
          .from('product_tags')
          .update({ order_index })
          .eq('id', id)
      );

      await Promise.all(updates);

      // Update local state
      setProductTags(prev => {
        const newTags = [...prev];
        tags.forEach(({ id, order_index }) => {
          const tagIndex = newTags.findIndex(tag => tag.id === id);
          if (tagIndex !== -1) {
            newTags[tagIndex] = { ...newTags[tagIndex], order_index };
          }
        });
        return newTags.sort((a, b) => a.order_index - b.order_index);
      });
    } catch (error) {
      console.error('Error reordering product tags:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao reordenar tags.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Product Complements Functions
  const addProductComplement = async (complementData: Omit<ProductComplement, 'id' | 'company_id' | 'order_index' | 'is_active' | 'created_at' | 'updated_at'>) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    try {
      const maxOrderIndex = productComplements.reduce((max, c) => Math.max(max, c.order_index), -1);

      const { data, error } = await supabase
        .from('product_complements')
        .insert({
          ...complementData,
          company_id: currentCompany.id,
          order_index: maxOrderIndex + 1,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      setProductComplements(prev => [...prev, data]);
      
      toast({
        title: 'Sucesso',
        description: 'Complemento criado com sucesso!',
      });
    } catch (error) {
      console.error('Error adding product complement:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao criar complemento.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateProductComplement = async (complementId: string, complementData: Partial<Omit<ProductComplement, 'id' | 'company_id' | 'created_at' | 'updated_at'>>) => {
    try {
      const { data, error } = await supabase
        .from('product_complements')
        .update(complementData)
        .eq('id', complementId)
        .select()
        .single();

      if (error) throw error;
      setProductComplements(prev => prev.map(c => c.id === complementId ? data : c));
      
      toast({
        title: 'Sucesso',
        description: 'Complemento atualizado com sucesso!',
      });
    } catch (error) {
      console.error('Error updating product complement:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar complemento.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteProductComplement = async (complementId: string) => {
    try {
      // First, remove complement from all menu items
      const { error: removeError } = await supabase
        .from('menu_item_complements')
        .delete()
        .eq('complement_id', complementId);

      if (removeError) throw removeError;

      // Then delete the complement
      const { error } = await supabase
        .from('product_complements')
        .delete()
        .eq('id', complementId);

      if (error) throw error;
      setProductComplements(prev => prev.filter(c => c.id !== complementId));
      
      toast({
        title: 'Sucesso',
        description: 'Complemento excluído com sucesso!',
      });
    } catch (error) {
      console.error('Error deleting product complement:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir complemento.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const toggleProductComplementStatus = async (complementId: string) => {
    try {
      const complement = productComplements.find(c => c.id === complementId);
      if (!complement) return;

      const { data, error } = await supabase
        .from('product_complements')
        .update({ is_active: !complement.is_active })
        .eq('id', complementId)
        .select()
        .single();

      if (error) throw error;
      setProductComplements(prev => prev.map(c => c.id === complementId ? data : c));
    } catch (error) {
      console.error('Error toggling product complement status:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao alterar status do complemento.',
        variant: 'destructive',
      });
    }
  };

  // Company Settings
  const updateCompanySettings = async (newSettings: Partial<CompanySettings>) => {
    setCompanySettings(prev => ({ ...prev, ...newSettings }));

    // Persist to Supabase
    if (currentCompany?.id && updateCompanyData) {
      try {
        // Map CompanySettings to Company interface
        const mappedData: Partial<Company> = {
          name: newSettings.name,
          email: newSettings.email,
          phone: newSettings.phone,
          address: newSettings.address,
          workingHours: newSettings.workingHours,
          logo_url: newSettings.logoUrl, // Map logoUrl to logo_url
          banner_image_url: newSettings.banner_image_url,
          instagram_description: newSettings.instagram_description,
          theme_mode: newSettings.theme_mode,
          enabled_layouts: newSettings.enabled_layouts,
          // Individual address fields
          street: newSettings.street,
          number: newSettings.number,
          complement: newSettings.complement,
          neighborhood: newSettings.neighborhood,
          city: newSettings.city,
          state: newSettings.state,
          zip_code: newSettings.zip_code,
          // Additional company fields
          legal_name: newSettings.legal_name,
          business_type: newSettings.business_type,
          document_type: newSettings.document_type,
          document_number: newSettings.document_number,
          // Social media and external links
          instagram_url: (newSettings as any).instagram_url,
          ifood_url: (newSettings as any).ifood_url,
          website_url: (newSettings as any).website_url,
          whatsapp_number: (newSettings as any).whatsapp_number,
          facebook_url: (newSettings as any).facebook_url,
        };
        console.log('💾 Atualizando configurações da empresa (mappedData):', mappedData);
        await updateCompanyData(mappedData);
        
        // Theme will be applied only in public menu, not in admin
        // Otimização: cache removido apenas quando necessário (mudanças visuais públicas)
        // O cache é mantido para navegação rápida no admin
        
        console.log('✅ Configurações da empresa atualizadas com sucesso');
      } catch (error) {
        console.error('❌ Erro ao atualizar configurações da empresa:', error);
        throw error;
      }
    }
  };

  // QR Code Settings
  const updateQRCodeSettings = async (updates: Partial<QRCodeSettings>) => {
    if (!currentCompany?.id) return;

    const newSettings = { ...qrCodeSettings, ...updates };
    
    // If URL is not provided in updates, auto-generate it
    if (!updates.url && updates.url !== '') {
      newSettings.url = `${window.location.protocol}//${window.location.host}/${currentCompany.slug}`;
    }

    setQrCodeSettings(newSettings);

    try {
      const payload = {
        company_id: currentCompany.id,
        url: newSettings.url,
        size: newSettings.size,
        include_company_name: newSettings.includeCompanyName,
        include_frame_text: newSettings.includeFrameText,
        frame_text: newSettings.frameText,
        transparent_background: newSettings.transparentBackground,
      };

      const { error } = await supabase
        .from('qr_code_settings')
        .upsert(payload, { onConflict: 'company_id' });

      if (error) {
        const code = (error as any).code;
        // Unique violation or conflict -> fallback to update
        if (code === '23505' || code === '409') {
          const { error: updateError } = await supabase
            .from('qr_code_settings')
            .update(payload)
            .eq('company_id', currentCompany.id);
          if (updateError) throw updateError;
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error updating QR code settings:', error);
      throw error;
    }
  };

  // Layout Settings
  const updateLayoutSettings = async (newSettings: Partial<LayoutSettings>) => {
    setLayoutSettings(prev => ({ ...prev, ...newSettings }));

    // Persist to Supabase directly
    if (currentCompany?.id) {
      try {
        const { error } = await supabase
          .from('companies')
          .update({
            layout_type: newSettings.layoutType,
          })
          .eq('id', currentCompany.id);

        if (error) throw error;
        
        // Update local cache without reloading everything
        if (updateCompanyData) {
          updateCompanyData({ layout_type: newSettings.layoutType } as any);
        }
      } catch (error) {
        console.error('Error updating layout settings:', error);
        throw error;
      }
    }
  };

  const value: AdminContextType = {
    menuItems,
    adminCategories,
    productTags,
    productComplements,
    companySettings,
    themeSettings,
    qrCodeSettings,
    layoutSettings,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    toggleMenuItemStatus,
    addCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    addProductTag,
    updateProductTag,
    deleteProductTag,
    reorderProductTags,
    addProductComplement,
    updateProductComplement,
    deleteProductComplement,
    toggleProductComplementStatus,
    updateCompanySettings,
    updateThemeSettings,
    updateQRCodeSettings,
    updateLayoutSettings,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

// Helper function to convert hex to HSL
const hexToHsl = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};